import { useEffect, useMemo, useState } from 'react';
import { useAction, useConvex, useMutation, useQuery } from 'convex/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import {
  AUDIENCE_PERSONAS,
  DEFAULT_AUDIENCE_PERSONA_IDS,
  computeAudienceAggregate,
  normalizeAudiencePersonaIds,
  getAudiencePersona,
} from '../../../shared/audienceTest';
import { getErrorMessage } from '../../utils/errors';
import { isValidProjectId } from '../../utils/projectIds';
import { compareProjectFilesByOrder } from '../../../shared/projectFileOrder';
import {
  CHALLENGE_PROJECT_DRAFT_PATH,
  CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH,
  CHALLENGE_PROJECT_SUBMISSION_PATH,
} from '../../../shared/challengeProjectSeed';
import './AudienceTestPage.css';

// Helper to get persona display info, handling custom personas
const getPersonaDisplay = (personaId, customPersonas = []) => {
  if (personaId.startsWith('custom:')) {
    const custom = customPersonas.find((p) => `custom:${p._id}` === personaId);
    return custom || { title: 'Custom', emoji: '🎯' };
  }
  return getAudiencePersona(personaId) || { id: personaId, title: personaId, emoji: '👤' };
};

const safeParseJson = (raw, fallback) => {
  if (!raw) return fallback;
  try { return JSON.parse(raw); } catch (_e) { return fallback; }
};

const formatRunTimestamp = (value) => {
  if (!value) return 'Not run yet';
  return new Date(value).toLocaleString();
};

const buildAIPrompt = (persona, result) => {
  if (!result) return '';

  const lines = [
    `I received feedback from a simulated "${persona?.title || 'reader'}" audience test on my manuscript. Here's what they identified:\n`,
  ];

  if (result.areasForImprovement?.length > 0) {
    lines.push(`**Areas for Improvement:**`);
    result.areasForImprovement.forEach((item) => lines.push(`- ${item}`));
    lines.push('');
  }

  if (result.dropOffPoints?.length > 0) {
    lines.push(`**Drop-off Points (where readers might stop reading):**`);
    result.dropOffPoints.forEach((pt) => {
      const location = pt.chapterPath === 'global' ? 'Overall' : pt.chapterPath;
      lines.push(`- ${location}: ${pt.reason}`);
    });
    lines.push('');
  }

  if (result.revisionPriorities?.length > 0) {
    lines.push(`**Revision Priorities:**`);
    result.revisionPriorities.forEach((item, i) => lines.push(`${i + 1}. ${item}`));
    lines.push('');
  }

  lines.push(`Please help me address these issues. Suggest specific changes I can make to improve my manuscript based on this feedback.`);

  return lines.join('\n');
};

const formatStatusLabel = (status) => {
  if (!status) return 'Pending';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const scoreClass = (value) => {
  if (typeof value !== 'number') return 'at-score__value--none';
  if (value >= 70) return 'at-score__value--good';
  if (value >= 40) return 'at-score__value--mid';
  return 'at-score__value--low';
};

const statScoreClass = (value) => {
  if (typeof value !== 'number' || value === 0) return '';
  if (value >= 70) return 'at-stat__value--good';
  if (value >= 40) return 'at-stat__value--mid';
  return 'at-stat__value--low';
};

function AudienceTestPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const convex = useConvex();
  const { projectId } = useParams();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;

  const startAudienceTestRun = useMutation(api.ai.startAudienceTestRun);
  const runAudienceTest = useAction(api.ai.runAudienceTest);
  const cancelAudienceTestRun = useMutation(api.ai.cancelAudienceTestRun);
  const createCustomPersona = useMutation(api.ai.createCustomAudiencePersona);
  const deleteCustomPersona = useMutation(api.ai.deleteCustomAudiencePersona);

  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const files = useQuery(api.files.listByProject, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const customPersonas = useQuery(api.ai.listCustomAudiencePersonas, safeProjectId ? { projectId: safeProjectId } : 'skip') || [];

  const [activeTab, setActiveTab] = useState('setup');
  const [scopeMode, setScopeMode] = useState('full');
  const [selectedPersonaIds, setSelectedPersonaIds] = useState(DEFAULT_AUDIENCE_PERSONA_IDS);
  const [selectedChapterPaths, setSelectedChapterPaths] = useState([]);
  const [activeRunId, setActiveRunId] = useState(null);
  const [activePersonaId, setActivePersonaId] = useState(DEFAULT_AUDIENCE_PERSONA_IDS[0]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLaunching, setIsLaunching] = useState(false);
  const [hasHydratedDefaults, setHasHydratedDefaults] = useState(false);
  const [runs, setRuns] = useState(undefined);
  const [estimate, setEstimate] = useState(null);
  const [backendMessage, setBackendMessage] = useState('');
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  // Custom persona modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreatingPersona, setIsCreatingPersona] = useState(false);
  const [newPersona, setNewPersona] = useState({
    title: '',
    emoji: '🎯',
    gender: 'any',
    ageRange: '25-44',
    familyStatus: 'any',
    profession: '',
    readingHabits: 'moderate',
    preferences: '',
  });

  const resetNewPersona = () => setNewPersona({
    title: '',
    emoji: '🎯',
    gender: 'any',
    ageRange: '25-44',
    familyStatus: 'any',
    profession: '',
    readingHabits: 'moderate',
    preferences: '',
  });

  const handleCreatePersona = async () => {
    if (!safeProjectId || isCreatingPersona) return;
    setIsCreatingPersona(true);
    try {
      await createCustomPersona({
        projectId: safeProjectId,
        title: newPersona.title || 'Custom Reader',
        emoji: newPersona.emoji,
        gender: newPersona.gender,
        ageRange: newPersona.ageRange,
        familyStatus: newPersona.familyStatus,
        profession: newPersona.profession,
        readingHabits: newPersona.readingHabits,
        preferences: newPersona.preferences,
      });
      setShowCreateModal(false);
      resetNewPersona();
    } catch (e) {
      setErrorMessage(getErrorMessage(e, 'Failed to create custom persona.'));
    } finally {
      setIsCreatingPersona(false);
    }
  };

  const handleDeletePersona = async (personaId) => {
    if (!window.confirm('Delete this custom audience?')) return;
    try {
      await deleteCustomPersona({ personaId });
      // Remove from selected if it was selected
      setSelectedPersonaIds((prev) => prev.filter((id) => id !== `custom:${personaId}`));
    } catch (e) {
      setErrorMessage(getErrorMessage(e, 'Failed to delete custom persona.'));
    }
  };

  const chapterFiles = useMemo(() => {
    if (!files) return [];
    const hasCanonicalChallengeSubmission = files.some((f) => f.type === 'file' && f.path === CHALLENGE_PROJECT_SUBMISSION_PATH);
    return files
      .filter((f) => {
        if (f.type !== 'file' || typeof f.content !== 'string' || f.content.trim().length === 0) return false;
        return (f.path.startsWith('Chapters/') && f.path !== CHALLENGE_PROJECT_DRAFT_PATH) ||
          (!hasCanonicalChallengeSubmission && f.path === CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH);
      })
      .sort(compareProjectFilesByOrder);
  }, [files]);

  const allChapterPaths = useMemo(() => chapterFiles.map((c) => c.path), [chapterFiles]);

  useEffect(() => {
    if (scopeMode === 'full') setSelectedChapterPaths(allChapterPaths);
  }, [scopeMode, allChapterPaths]);

  const latestRun = runs?.[0] ?? null;
  const runForDisplay = useMemo(() => {
    if (!runs || runs.length === 0) return null;
    if (activeRunId) return runs.find((r) => r._id === activeRunId) ?? runs[0];
    return runs[0];
  }, [runs, activeRunId]);

  useEffect(() => {
    if (hasHydratedDefaults || files === undefined || runs === undefined) return;
    if (latestRun) {
      const latestPaths = Array.isArray(latestRun.scopeChapterPaths) ? latestRun.scopeChapterPaths : [];
      const validPaths = latestPaths.filter((p) => allChapterPaths.includes(p));
      const latestPersonas = normalizeAudiencePersonaIds(latestRun.selectedPersonaIds);
      setScopeMode(validPaths.length > 0 && validPaths.length < allChapterPaths.length ? 'selected' : 'full');
      setSelectedChapterPaths(validPaths.length > 0 ? validPaths : allChapterPaths);
      setSelectedPersonaIds(latestPersonas.length > 0 ? latestPersonas : DEFAULT_AUDIENCE_PERSONA_IDS);
      setActiveRunId(latestRun._id);
    } else {
      setSelectedChapterPaths(allChapterPaths);
    }
    setHasHydratedDefaults(true);
  }, [allChapterPaths, files, hasHydratedDefaults, latestRun, runs]);

  useEffect(() => {
    const preferred = normalizeAudiencePersonaIds(runForDisplay?.selectedPersonaIds ?? selectedPersonaIds);
    if (!preferred.includes(activePersonaId)) setActivePersonaId(preferred[0] ?? DEFAULT_AUDIENCE_PERSONA_IDS[0]);
  }, [activePersonaId, runForDisplay?.selectedPersonaIds, selectedPersonaIds]);

  useEffect(() => {
    if (!safeProjectId) { setRuns([]); return undefined; }
    let cancelled = false;
    const load = async () => {
      try {
        const next = await convex.query(api.ai.listAudienceTestRuns, { projectId: safeProjectId, limit: 10 });
        if (!cancelled) { setRuns(next); setBackendMessage(''); }
      } catch (e) {
        if (!cancelled) { setRuns([]); setBackendMessage(getErrorMessage(e, 'Audience Test backend unavailable.')); }
      }
    };
    load();
    const id = window.setInterval(load, latestRun?.status === 'running' ? 2500 : 8000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [convex, latestRun?.status, safeProjectId]);

  useEffect(() => {
    if (!safeProjectId || selectedPersonaIds.length === 0) { setEstimate(null); return undefined; }
    let cancelled = false;
    const load = async () => {
      try {
        const next = await convex.query(api.ai.estimateAudienceTest, {
          projectId: safeProjectId,
          personaIds: selectedPersonaIds,
          ...(scopeMode === 'selected' ? { chapterPaths: selectedChapterPaths } : {}),
        });
        if (!cancelled) { setEstimate(next); setBackendMessage(''); }
      } catch (e) {
        if (!cancelled) { setEstimate(null); setBackendMessage(getErrorMessage(e, 'Estimate unavailable.')); }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [convex, safeProjectId, scopeMode, selectedChapterPaths, selectedPersonaIds]);

  const runPersonaResults = useMemo(() => safeParseJson(runForDisplay?.personaResultsJson, {}), [runForDisplay?.personaResultsJson]);
  const runPersonaStatuses = runForDisplay?.personaStatuses ?? {};
  const runSelectedPersonaIds = normalizeAudiencePersonaIds(runForDisplay?.selectedPersonaIds);
  const runAggregate = useMemo(() => {
    const parsed = safeParseJson(runForDisplay?.aggregateJson, null);
    if (parsed) return parsed;
    return computeAudienceAggregate(runPersonaResults, runPersonaStatuses, runSelectedPersonaIds);
  }, [runForDisplay?.aggregateJson, runPersonaResults, runPersonaStatuses, runSelectedPersonaIds]);

  const activeResult = runPersonaResults[activePersonaId] ?? null;
  const activeStatus = runPersonaStatuses[activePersonaId] ?? 'pending';
  const isRunBusy = isLaunching || runForDisplay?.status === 'running';
  const isBackendReady = !backendMessage;
  const displayPersonaIds = runSelectedPersonaIds.length > 0 ? runSelectedPersonaIds : selectedPersonaIds;

  const togglePersona = (id) => {
    if (isRunBusy) return;
    setSelectedPersonaIds((cur) => {
      if (cur.includes(id)) {
        return cur.filter((x) => x !== id);
      }
      // Separate custom IDs from built-in IDs
      const customIds = [...cur, id].filter((x) => x.startsWith('custom:'));
      const builtInIds = [...cur, id].filter((x) => !x.startsWith('custom:'));
      // Normalize built-in IDs, keep custom IDs as-is
      return [...normalizeAudiencePersonaIds(builtInIds), ...customIds];
    });
  };

  const toggleChapter = (path) => {
    if (isRunBusy) return;
    setSelectedChapterPaths((cur) =>
      cur.includes(path) ? cur.filter((p) => p !== path) : [...cur, path].sort((a, b) => a.localeCompare(b)),
    );
  };

  const handleCopyPrompt = async () => {
    const persona = getPersonaDisplay(activePersonaId, customPersonas);
    const prompt = buildAIPrompt(persona, activeResult);
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 2000);
    } catch (e) {
      console.error('Failed to copy prompt:', e);
    }
  };

  const handleRun = async () => {
    if (!safeProjectId || isRunBusy) return;
    if (selectedPersonaIds.length === 0) { setErrorMessage('Select at least one persona.'); return; }
    if (scopeMode === 'selected' && selectedChapterPaths.length === 0) { setErrorMessage('Select at least one chapter.'); return; }
    setErrorMessage('');
    setIsLaunching(true);
    try {
      // Pass all persona IDs directly (both built-in and custom:id format)
      // Backend now handles custom:id format natively
      const started = await startAudienceTestRun({
        projectId: safeProjectId,
        personaIds: selectedPersonaIds,
        ...(scopeMode === 'selected' ? { chapterPaths: selectedChapterPaths } : {}),
      });
      setActiveRunId(started.runId);
      setActivePersonaId(started.selectedPersonaIds[0] ?? DEFAULT_AUDIENCE_PERSONA_IDS[0]);
      setActiveTab('results');
      await runAudienceTest({ runId: started.runId });
    } catch (e) {
      setErrorMessage(getErrorMessage(e, 'Audience Test could not be started.'));
    } finally {
      setIsLaunching(false);
    }
  };

  const handleCancel = async () => {
    if (!runForDisplay || runForDisplay.status !== 'running') return;
    try {
      await cancelAudienceTestRun({ runId: runForDisplay._id });
    } catch (e) {
      setErrorMessage(getErrorMessage(e, 'Failed to cancel run.'));
    }
  };

  // ── Guards ──
  if (!safeProjectId) {
    return <main className="at-page at-loading"><div>{t('common.invalidProjectLink')} <button className="btn btn-secondary" onClick={() => navigate('/projects')}>{t('common.back')}</button></div></main>;
  }
  if (project === undefined || files === undefined || runs === undefined) {
    return <main className="at-page at-loading">{t('audienceTest.loadingTest')}</main>;
  }
  if (!project) {
    return <main className="at-page at-loading">{t('common.projectNotFound')}</main>;
  }

  const completedCount = Object.values(runPersonaStatuses).filter((s) => s === 'completed').length;
  const totalPersonas = displayPersonaIds.length;

  return (
    <main className="at-page" aria-label={t('audienceTest.title')}>

      {/* Header */}
      <header className="at-header">
        <div className="at-header-left">
          <h1>{t('audienceTest.title')} <span>— {project.title}</span></h1>
          <p className="at-header-desc">{t('audienceTest.description')}</p>
        </div>
        <div className="at-actions">
          <button className="btn btn-secondary" onClick={() => navigate(`/projects/${safeProjectId}`)}>{t('common.back')}</button>
          <button className="btn btn-secondary" onClick={() => navigate(`/projects/${safeProjectId}/editor`)}>{t('common.editor')}</button>
          {runForDisplay?.status === 'running' && !isLaunching && (
            <button className="btn btn-secondary" onClick={handleCancel}>{t('common.cancel', 'Cancel')}</button>
          )}
          <button className="btn btn-primary" onClick={handleRun} disabled={isRunBusy || !isBackendReady}>
            {isRunBusy ? t('common.running') : t('audienceTest.runTest')}
          </button>
        </div>
      </header>

      {/* Alerts */}
      {(errorMessage || backendMessage) && <div className="at-alert">{errorMessage || backendMessage}</div>}

      {/* Tabs */}
      <nav className="at-tabs" role="tablist">
        <button role="tab" aria-selected={activeTab === 'setup'} className={`at-tab ${activeTab === 'setup' ? 'active' : ''}`} onClick={() => setActiveTab('setup')}>{t('audienceTest.tabs.setup')}</button>
        <button role="tab" aria-selected={activeTab === 'results'} className={`at-tab ${activeTab === 'results' ? 'active' : ''}`} onClick={() => setActiveTab('results')}>
          {t('audienceTest.tabs.results')}{completedCount > 0 && <strong>{completedCount}/{totalPersonas}</strong>}
        </button>
        <button role="tab" aria-selected={activeTab === 'history'} className={`at-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
          {t('audienceTest.tabs.history')}{runs && runs.length > 0 && <strong>{runs.length}</strong>}
        </button>
      </nav>

      <section className="at-surface">

      {/* ══ Setup Tab ══ */}
      {activeTab === 'setup' ? (
        <div className="at-fade" role="tabpanel" aria-label={t('audienceTest.tabs.setup')}>

          {/* Stats */}
          <div className="at-stats">
            <div className="at-stat">
              <span className="at-stat__label">{t('audienceTest.stats.avgScore')}</span>
              <strong className={`at-stat__value ${statScoreClass(runAggregate?.averageScores?.overallScore)}`}>{runAggregate?.averageScores?.overallScore ?? '—'}</strong>
              <span className="at-stat__sub">{runForDisplay ? formatStatusLabel(runForDisplay.status) : t('draftReview.noRun')}</span>
            </div>
            <div className="at-stat">
              <span className="at-stat__label">{t('audienceTest.stats.personas')}</span>
              <strong className="at-stat__value">{selectedPersonaIds.length}</strong>
              <span className="at-stat__sub">{selectedPersonaIds.map((id) => getPersonaDisplay(id, customPersonas).title).join(', ') || t('common.none')}</span>
            </div>
            <div className="at-stat">
              <span className="at-stat__label">{t('audienceTest.stats.scope')}</span>
              <strong className="at-stat__value">{scopeMode === 'full' ? allChapterPaths.length : selectedChapterPaths.length}</strong>
              <span className="at-stat__sub">{scopeMode === 'full' ? t('audienceTest.stats.fullDraft') : t('audienceTest.stats.selectedChapters')}</span>
            </div>
            <div className="at-stat">
              <span className="at-stat__label">{t('audienceTest.stats.estCredits')}</span>
              <strong className="at-stat__value">{estimate?.estimatedCredits?.toFixed(1) ?? '—'}</strong>
              <span className="at-stat__sub">{estimate ? t('audienceTest.stats.personaCount', { count: estimate.personaCount }) : t('audienceTest.stats.calculating')}</span>
            </div>
          </div>

          {/* Persona Picker */}
          <span className="at-section-label">{t('audienceTest.readerPersonas')}</span>
          <div className="at-personas">
            {/* Built-in personas */}
            {AUDIENCE_PERSONAS.map((persona) => (
              <button
                key={persona.id}
                type="button"
                className={`at-persona ${selectedPersonaIds.includes(persona.id) ? 'selected' : ''}`}
                onClick={() => togglePersona(persona.id)}
                disabled={isRunBusy}
              >
                <div className="at-persona__head">
                  <span className="at-persona__emoji">{persona.emoji}</span>
                  <span className="at-persona__name">{persona.title}</span>
                </div>
                <span className="at-persona__meta">{persona.demographics}</span>
                <span className="at-persona__meta">{persona.readingProfile}</span>
                <p className="at-persona__taste">{persona.taste}</p>
              </button>
            ))}

            {/* Custom personas */}
            {customPersonas.map((persona) => (
              <button
                key={persona._id}
                type="button"
                className={`at-persona at-persona--custom ${selectedPersonaIds.includes(`custom:${persona._id}`) ? 'selected' : ''}`}
                onClick={() => togglePersona(`custom:${persona._id}`)}
                disabled={isRunBusy}
              >
                <div className="at-persona__head">
                  <span className="at-persona__emoji">{persona.emoji}</span>
                  <span className="at-persona__name">{persona.title}</span>
                  <button
                    type="button"
                    className="at-persona__delete"
                    onClick={(e) => { e.stopPropagation(); handleDeletePersona(persona._id); }}
                    title={t('common.delete')}
                  >
                    ×
                  </button>
                </div>
                <span className="at-persona__meta">{persona.demographics}</span>
                <span className="at-persona__meta">{persona.readingProfile}</span>
                <p className="at-persona__taste">{persona.taste}</p>
              </button>
            ))}

            {/* Add Custom button */}
            <button
              type="button"
              className="at-persona at-persona--add"
              onClick={() => setShowCreateModal(true)}
              disabled={isRunBusy}
            >
              <div className="at-persona__add-content">
                <span className="at-persona__add-icon">+</span>
                <span className="at-persona__add-label">{t('audienceTest.customAudience.createButton')}</span>
              </div>
            </button>
          </div>

          {/* Chapter Scope */}
          <div className="at-scope">
            <div className="at-scope-bar">
              <div className="at-scope-bar__left">
                <span className="at-section-label" style={{ margin: 0 }}>{t('audienceTest.chapterScope')}</span>
                <span className="at-scope-bar__text">
                  {scopeMode === 'full' ? t('audienceTest.allChapters') : t('audienceTest.selectedOf', { selected: selectedChapterPaths.length, total: allChapterPaths.length })}
                </span>
              </div>
              <div className="at-pills">
                <button type="button" className={`at-pill ${scopeMode === 'full' ? 'active' : ''}`} onClick={() => setScopeMode('full')} disabled={isRunBusy}>{t('audienceTest.fullDraftBtn')}</button>
                <button type="button" className={`at-pill ${scopeMode === 'selected' ? 'active' : ''}`} onClick={() => setScopeMode('selected')} disabled={isRunBusy}>{t('audienceTest.pickChaptersBtn')}</button>
              </div>
            </div>
            {scopeMode === 'selected' && (
              <div className="at-chapter-chips">
                {chapterFiles.map((ch) => (
                  <button key={ch.path} type="button" className={`at-chip ${selectedChapterPaths.includes(ch.path) ? 'active' : ''}`} onClick={() => toggleChapter(ch.path)} disabled={isRunBusy}>
                    {ch.path.replace('Chapters/', '')}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* ══ Results Tab ══ */}
      {activeTab === 'results' ? (
        <div className="at-fade" role="tabpanel" aria-label={t('audienceTest.tabs.results')}>
          {!runForDisplay ? (
            <div className="at-empty">
              <h3>{t('audienceTest.noTestResults')}</h3>
              <p>{t('audienceTest.noTestResultsDesc')}</p>
            </div>
          ) : (
            <div className="at-results">
              {/* Persona Rail */}
              <aside className="at-rail">
                <div className="at-rail__head">
                  <h2>{t('audienceTest.personasRail')}</h2>
                </div>
                <div className="at-rail-list">
                  {displayPersonaIds.map((pid) => {
                    const persona = getPersonaDisplay(pid, customPersonas);
                    const result = runPersonaResults[pid];
                    const status = runPersonaStatuses[pid] ?? 'pending';
                    return (
                      <button key={pid} type="button" className={`at-rail-card ${activePersonaId === pid ? 'active' : ''}`} onClick={() => setActivePersonaId(pid)}>
                        <div className="at-rail-card__top">
                          <span className="at-rail-card__emoji">{persona.emoji}</span>
                          <span className="at-rail-card__name">{persona.title}</span>
                        </div>
                        <div className="at-rail-card__bottom">
                          <span className={`at-status at-status--${status}`}>{formatStatusLabel(status)}</span>
                          <span className="at-rail-card__score">{result ? `${result.overallScore}/100` : '—'}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </aside>

              {/* Main Results */}
              <div className="at-main">
                {/* Scores */}
                <div className="at-scores">
                  {[
                    { label: t('audienceTest.scores.overall'), value: activeResult?.overallScore },
                    { label: t('audienceTest.scores.hook'), value: activeResult?.hookScore },
                    { label: t('audienceTest.scores.emotion'), value: activeResult?.emotionScore },
                    { label: t('audienceTest.scores.clarity'), value: activeResult?.clarityScore },
                  ].map((m) => (
                    <div key={m.label} className="at-score">
                      <span className="at-score__label">{m.label}</span>
                      <strong className={`at-score__value ${scoreClass(m.value)}`}>{m.value ?? '—'}</strong>
                    </div>
                  ))}
                </div>

                {!activeResult ? (
                  <div className="at-empty">
                    <h3>{getPersonaDisplay(activePersonaId, customPersonas).title}</h3>
                    <p>{activeStatus === 'running' ? t('audienceTest.analyzingManuscript') : activeStatus === 'failed' ? t('audienceTest.personaFailed') : t('audienceTest.waitingForResults')}</p>
                  </div>
                ) : (
                  <>
                    {/* Ask AI Button */}
                    <div className="at-ask-ai">
                      <button
                        className="btn btn-secondary at-ask-ai-btn"
                        onClick={handleCopyPrompt}
                        disabled={!activeResult?.areasForImprovement?.length && !activeResult?.revisionPriorities?.length}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        {copiedPrompt ? t('common.copied') : t('audienceTest.askAI')}
                      </button>
                      <span className="at-ask-ai-hint">{t('audienceTest.askAIHint')}</span>
                    </div>

                    {/* Quote */}
                    <div className="at-quote">
                      <span className="at-quote__label">{t('audienceTest.featuredReaction')}</span>
                      <p className="at-quote__text">"{activeResult.summaryQuote}"</p>
                    </div>

                    {/* Strengths + Weaknesses */}
                    <div className="at-feedback-grid">
                      <div className="at-feedback at-feedback--strengths">
                        <div className="at-feedback__label">{t('audienceTest.strengths')}</div>
                        <ul>
                          {activeResult.strengths.length > 0 ? activeResult.strengths.map((s) => <li key={s}>{s}</li>) : <li>{t('audienceTest.noneReturned')}</li>}
                        </ul>
                      </div>
                      <div className="at-feedback at-feedback--weaknesses">
                        <div className="at-feedback__label">{t('audienceTest.areasForImprovement')}</div>
                        <ul>
                          {activeResult.areasForImprovement.length > 0 ? activeResult.areasForImprovement.map((s) => <li key={s}>{s}</li>) : <li>{t('audienceTest.noneReturned')}</li>}
                        </ul>
                      </div>
                    </div>

                    {/* Drop-offs */}
                    <div className="at-feedback at-feedback--dropoffs">
                      <div className="at-feedback__label">{t('audienceTest.dropOffPoints')}</div>
                      <ul>
                        {activeResult.dropOffPoints.length > 0 ? activeResult.dropOffPoints.map((pt) => (
                          <li key={`${pt.chapterPath}-${pt.reason}`}>
                            <strong>{pt.chapterPath === 'global' ? t('audienceTest.global') : pt.chapterPath}</strong>
                            <span>{pt.reason}</span>
                          </li>
                        )) : <li>{t('audienceTest.noDropOffs')}</li>}
                      </ul>
                    </div>

                    {/* Priorities */}
                    <div className="at-feedback at-feedback--priorities">
                      <div className="at-feedback__label">{t('audienceTest.revisionPriorities')}</div>
                      <ol>
                        {activeResult.revisionPriorities.length > 0 ? activeResult.revisionPriorities.map((s) => <li key={s}>{s}</li>) : <li>{t('audienceTest.noneReturned')}</li>}
                      </ol>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* ══ History Tab ══ */}
      {activeTab === 'history' ? (
        <div className="at-fade" role="tabpanel" aria-label={t('audienceTest.tabs.history')}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', marginBottom: 16 }}>{t('audienceTest.runHistory')}</h2>
          {!runs || runs.length === 0 ? (
            <div className="at-empty">
              <h3>{t('audienceTest.noRunsYet')}</h3>
              <p>{t('audienceTest.noRunsYetDesc')}</p>
            </div>
          ) : (
            <div className="at-history-list">
              {runs.map((run) => {
                const agg = safeParseJson(run.aggregateJson, null) || computeAudienceAggregate(
                  safeParseJson(run.personaResultsJson, {}),
                  run.personaStatuses ?? {},
                  normalizeAudiencePersonaIds(run.selectedPersonaIds),
                );
                const personas = normalizeAudiencePersonaIds(run.selectedPersonaIds);
                return (
                  <article key={run._id} className={`at-history-item ${run._id === runForDisplay?._id ? 'active' : ''}`}>
                    <div className="at-history-main">
                      <div>
                        <h3>{formatRunTimestamp(run.createdAt)}</h3>
                        <p className="at-history-meta">
                          {formatStatusLabel(run.status)} · {t('audienceTest.historyPersonas', { count: personas.length, plural: personas.length !== 1 ? 's' : '' })} · {t('audienceTest.historyChapters', { count: run.scopeChapterPaths?.length ?? 0 })}
                        </p>
                      </div>
                      <button className="btn btn-secondary" onClick={() => { setActiveRunId(run._id); setActiveTab('results'); }}>{t('common.view')}</button>
                    </div>
                    {agg && agg.completedPersonaCount > 0 && (
                      <div className="at-history-scores">
                        <span className="at-history-score">{t('audienceTest.historyScoreOverall')}: <strong>{agg.averageScores.overallScore}</strong></span>
                        <span className="at-history-score">{t('audienceTest.historyScoreHook')}: <strong>{agg.averageScores.hookScore}</strong></span>
                        <span className="at-history-score">{t('audienceTest.historyScoreEmotion')}: <strong>{agg.averageScores.emotionScore}</strong></span>
                        <span className="at-history-score">{t('audienceTest.historyScoreClarity')}: <strong>{agg.averageScores.clarityScore}</strong></span>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      </section>

      {/* Create Custom Persona Modal */}
      {showCreateModal && (
        <div className="at-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="at-modal" onClick={(e) => e.stopPropagation()}>
            <div className="at-modal__header">
              <h2>{t('audienceTest.customAudience.title')}</h2>
              <button className="at-modal__close" onClick={() => setShowCreateModal(false)} aria-label={t('common.close')}>×</button>
            </div>
            <div className="at-modal__body">
              <div className="at-modal__row">
                <div className="at-modal__field">
                  <label>{t('audienceTest.customAudience.fields.name')}</label>
                  <input
                    type="text"
                    value={newPersona.title}
                    onChange={(e) => setNewPersona({ ...newPersona, title: e.target.value })}
                    placeholder={t('audienceTest.customAudience.placeholders.name')}
                  />
                </div>
                <div className="at-modal__field at-modal__field--emoji">
                  <label>{t('audienceTest.customAudience.fields.emoji')}</label>
                  <input
                    type="text"
                    value={newPersona.emoji}
                    onChange={(e) => setNewPersona({ ...newPersona, emoji: e.target.value })}
                    maxLength={2}
                  />
                </div>
              </div>
              <div className="at-modal__row">
                <div className="at-modal__field">
                  <label>{t('audienceTest.customAudience.fields.gender')}</label>
                  <select value={newPersona.gender} onChange={(e) => setNewPersona({ ...newPersona, gender: e.target.value })}>
                    <option value="any">{t('audienceTest.customAudience.options.gender.any')}</option>
                    <option value="female">{t('audienceTest.customAudience.options.gender.female')}</option>
                    <option value="male">{t('audienceTest.customAudience.options.gender.male')}</option>
                  </select>
                </div>
                <div className="at-modal__field">
                  <label>{t('audienceTest.customAudience.fields.ageRange')}</label>
                  <select value={newPersona.ageRange} onChange={(e) => setNewPersona({ ...newPersona, ageRange: e.target.value })}>
                    <option value="16-24">{t('audienceTest.customAudience.options.ageRange.16_24')}</option>
                    <option value="25-34">{t('audienceTest.customAudience.options.ageRange.25_34')}</option>
                    <option value="35-44">{t('audienceTest.customAudience.options.ageRange.35_44')}</option>
                    <option value="45-54">{t('audienceTest.customAudience.options.ageRange.45_54')}</option>
                    <option value="55+">{t('audienceTest.customAudience.options.ageRange.55_plus')}</option>
                    <option value="25-44">{t('audienceTest.customAudience.options.ageRange.25_44')}</option>
                  </select>
                </div>
                <div className="at-modal__field">
                  <label>{t('audienceTest.customAudience.fields.familyStatus')}</label>
                  <select value={newPersona.familyStatus} onChange={(e) => setNewPersona({ ...newPersona, familyStatus: e.target.value })}>
                    <option value="any">{t('audienceTest.customAudience.options.familyStatus.any')}</option>
                    <option value="single">{t('audienceTest.customAudience.options.familyStatus.single')}</option>
                    <option value="married">{t('audienceTest.customAudience.options.familyStatus.married')}</option>
                    <option value="single-parent">{t('audienceTest.customAudience.options.familyStatus.singleParent')}</option>
                    <option value="parent">{t('audienceTest.customAudience.options.familyStatus.parent')}</option>
                  </select>
                </div>
              </div>
              <div className="at-modal__row">
                <div className="at-modal__field">
                  <label>{t('audienceTest.customAudience.fields.readingHabits')}</label>
                  <select value={newPersona.readingHabits} onChange={(e) => setNewPersona({ ...newPersona, readingHabits: e.target.value })}>
                    <option value="light">{t('audienceTest.customAudience.options.readingHabits.light')}</option>
                    <option value="moderate">{t('audienceTest.customAudience.options.readingHabits.moderate')}</option>
                    <option value="heavy">{t('audienceTest.customAudience.options.readingHabits.heavy')}</option>
                  </select>
                </div>
                <div className="at-modal__field at-modal__field--wide">
                  <label>{t('audienceTest.customAudience.fields.profession')}</label>
                  <input
                    type="text"
                    value={newPersona.profession}
                    onChange={(e) => setNewPersona({ ...newPersona, profession: e.target.value })}
                    placeholder={t('audienceTest.customAudience.placeholders.profession')}
                  />
                </div>
              </div>
              <div className="at-modal__field at-modal__field--full">
                <label>{t('audienceTest.customAudience.fields.moreInfo')}</label>
                <textarea
                  value={newPersona.preferences}
                  onChange={(e) => setNewPersona({ ...newPersona, preferences: e.target.value })}
                  placeholder={t('audienceTest.customAudience.placeholders.moreInfo')}
                  rows={3}
                />
              </div>
            </div>
            <div className="at-modal__footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleCreatePersona} disabled={isCreatingPersona}>
                {isCreatingPersona ? t('common.creating') : t('audienceTest.customAudience.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default AudienceTestPage;
